/**
 * NewLookupDialog — the inline "New {record}" quick-add opened from a lookup
 * field's green ＋ (prototype `openNewItem` / `NEW_FORMS`).
 *
 * Legacy parity with a deliberate simplification carried over from the
 * prototype (decision 2026-07-18): Client and Contact are QUICK-ADDs — only
 * what registration and the COC need.  The complete records (account
 * management, delivery flags, photos, child tabs …) stay in their master-data
 * screens, which the dialog says out loud.
 *
 * On OK the new row is prepended to the in-memory lookup and selected into the
 * field that opened it.
 * TODO(AI-29131): POST the new record to its master-data endpoint instead of
 * mutating the local catalog, then re-read the lookup.
 */
import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { LOOKUPS, NEWABLE, type SrLookupKey } from './srData';

/** One field in a quick-add form. */
interface QuickField {
  label: string;
  required?: boolean;
  kind?: 'text' | 'textarea' | 'radio';
  /** Radio choices (first is the default). */
  choices?: string[];
  placeholder?: string;
  readOnlyValue?: string;
}

interface QuickForm {
  fields: QuickField[];
  width?: number;
  /** 2 = the prototype's wide two-column layout (Project only). */
  columns?: 1 | 2;
  footnote?: string;
  /** Build the lookup row (column order must match the lookup's `cols`). */
  toRow: (get: (label: string) => string) => string[];
}

function quickForm(kind: SrLookupKey, clientName: string): QuickForm {
  switch (kind) {
    case 'client':
      return {
        width: 520,
        footnote: 'Quick add — the full client record (account management, contacts, projects…) is managed in Client & Project ▸ Clients.',
        fields: [
          { label: 'Client', required: true },
          { label: 'Address 1', placeholder: 'Street address (shows on the COC)' },
          { label: 'Phone' },
          { label: 'Email' },
        ],
        toRow: g => [g('Client'), g('Address 1'), g('Phone')],
      };
    case 'contact':
      return {
        width: 520,
        footnote: 'Quick add — full contact details (address, delivery flags, photo…) are managed in Client & Project ▸ Contacts.',
        fields: [
          { label: 'First Name', required: true },
          { label: 'Last Name' },
          // The prototype's radio row carries NO label text.
          { label: '', kind: 'radio', choices: ['Report Contact', 'Invoice Contact'] },
          { label: 'Email' },
          { label: 'Mobile Phone' },
          { label: 'Client', readOnlyValue: clientName || 'N/A' },
        ],
        toRow: g => [[g('First Name'), g('Last Name')].filter(Boolean).join(' '), g('Email')],
      };
    case 'project':
      // The one WIDE quick-add — two columns, exactly the prototype's field set.
      return {
        width: 820,
        columns: 2,
        fields: [
          { label: 'Project ID', required: true },
          { label: 'Project Name' },
          { label: 'Client', readOnlyValue: clientName || 'N/A' },
          { label: 'Project Location', kind: 'textarea' },
          { label: 'Project City' },
          { label: 'To' },
          { label: 'CC' },
          { label: 'Project Overview' },
          { label: 'Project Source' },
          { label: 'Sample Retention' },
          { label: 'Comment', kind: 'textarea' },
        ],
        toRow: g => [
          g('Project ID'),
          g('Project Name') || g('Project ID'),
          g('Project Location').replace(/\n/g, ' '),
        ],
      };
    case 'collector':
      return {
        width: 520,
        fields: [
          { label: 'Full Name' },
          { label: 'First Name', required: true },
          { label: 'Last Name' },
          { label: 'Client Name', readOnlyValue: clientName || 'N/A' },
          { label: 'Mobile Phone' },
          { label: 'Comment', kind: 'textarea' },
        ],
        toRow: g => [[g('First Name'), g('Last Name')].filter(Boolean).join(' ') || g('Full Name')],
      };
    case 'relinq':
      return {
        width: 520,
        fields: [
          { label: 'Relinquished By', required: true },
          { label: 'Relinquished Code' },
          { label: 'Comment', kind: 'textarea' },
        ],
        toRow: g => [g('Relinquished By')],
      };
    case 'category':
      return {
        width: 520,
        fields: [
          { label: 'Project Category', required: true },
          { label: 'Description' },
          { label: 'Non Commercial', kind: 'radio', choices: ['None', 'Yes'] },
        ],
        toRow: g => [g('Project Category')],
      };
    case 'inact':
      return {
        width: 520,
        fields: [
          { label: 'Action', required: true },
          { label: 'Inactivation Reason', required: true },
        ],
        toRow: g => [g('Action')],
      };
    default: {
      // Generic one-field-per-column dialog for the simple code lists.
      const cols = LOOKUPS[kind].cols;
      return {
        fields: cols.map((c, i) => ({ label: c, required: i === 0 })),
        toRow: g => cols.map(c => g(c)),
      };
    }
  }
}

export interface NewLookupDialogProps {
  /** Which lookup to add to; null = closed. */
  kind: SrLookupKey | null;
  /** The currently-selected client, shown read-only on the child forms. */
  clientName: string;
  onClose: () => void;
  /** The new row, ready to prepend to the lookup and select into the field. */
  onAdd: (kind: SrLookupKey, row: string[]) => void;
  /** Shared warning (routes to the blocking modal — §18.6). */
  onWarn: (message: string) => void;
}

export function NewLookupDialog({ kind, clientName, onClose, onAdd, onWarn }: NewLookupDialogProps) {
  const form = useMemo(() => (kind ? quickForm(kind, clientName) : null), [kind, clientName]);
  const [vals, setVals] = useState<Record<string, string>>({});

  // Reset the form each time it opens (radios default to their first choice).
  useEffect(() => {
    if (!form) return;
    const seed: Record<string, string> = {};
    for (const f of form.fields) {
      seed[f.label] = f.readOnlyValue ?? (f.kind === 'radio' ? (f.choices?.[0] ?? '') : '');
    }
    setVals(seed);
  }, [form]);

  if (!kind || !form) return null;

  const get = (label: string) => (vals[label] ?? '').trim();
  const set = (label: string, v: string) => setVals(s => ({ ...s, [label]: v }));

  const submit = () => {
    const missing = form.fields.find(f => f.required && !get(f.label));
    if (missing) { onWarn(`Enter the ${missing.label}.`); return; }
    onAdd(kind, form.toRow(get));
  };

  return (
    <AppDialog
      open
      /* Prototype titles are the bare entity name, with no icon. */
      title={NEWABLE[kind] ?? kind}
      width={form.width ?? 460}
      onClose={onClose}
      footer={
        <>
          <button className="ca-btn" onClick={onClose}>Cancel</button>
          <button className="ca-btn ca-btn-solid" onClick={submit}>
            <span className="tb-i">💾</span> OK
          </button>
        </>
      }
    >
      <div
        className="sr-fgrid"
        style={{
          gridTemplateColumns: form.columns === 2
            ? '150px minmax(0, 1fr) 150px minmax(0, 1fr)'   /* prototype n2col */
            : '150px minmax(0, 1fr)',
        }}
      >
        {form.fields.map(f => (
          <FieldRow key={f.label} field={f} value={vals[f.label] ?? ''} onChange={v => set(f.label, v)} />
        ))}
      </div>
      {form.footnote && <div className="sr-hint-inline" style={{ marginTop: 8 }}>{form.footnote}</div>}
    </AppDialog>
  );
}

function FieldRow({ field, value, onChange }: {
  field: QuickField; value: string; onChange: (v: string) => void;
}) {
  const ro = field.readOnlyValue !== undefined;
  return (
    <>
      <label className={field.required ? 'req' : undefined}>{field.label}</label>
      {field.kind === 'radio' ? (
        <span style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          {(field.choices ?? []).map(c => (
            <label key={c} style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 12.5, cursor: 'pointer', textAlign: 'left', fontWeight: 400 }}>
              <input type="radio" name={`sr-new-${field.label}`} checked={value === c}
                onChange={() => onChange(c)} />
              {c}
            </label>
          ))}
        </span>
      ) : field.kind === 'textarea' ? (
        <textarea rows={2} value={value} onChange={e => onChange(e.target.value)} />
      ) : (
        <input
          type="text"
          value={value}
          readOnly={ro}
          placeholder={field.placeholder}
          className={field.required && !value.trim() ? 'sr-req-empty' : undefined}
          onChange={e => onChange(e.target.value)}
        />
      )}
    </>
  );
}
